import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, DollarSign, Mail, CheckCircle, Smartphone, Download,
  Link2, Calculator, Zap, Settings
} from 'lucide-react';

// ============================================
// Small helpers so the page below stays readable
// ============================================
const CALLOUT_STYLES = {
  blue: 'bg-blue-50 border-blue-200 text-blue-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  green: 'bg-green-50 border-green-200 text-green-900',
};

const Callout = ({ tone = 'blue', children }) => (
  <div className={`border rounded-lg p-3 text-sm ${CALLOUT_STYLES[tone]}`}>{children}</div>
);

const Step = ({ n, color = 'bg-blue-600', title, children }) => (
  <div className="flex items-start gap-4">
    <div className={`w-10 h-10 ${color} text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0`}>
      {n}
    </div>
    <div className="flex-1">
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  </div>
);

const StatusRow = ({ label, badgeClass, title, text }) => (
  <div className="flex items-start gap-3">
    <span className={`px-3 py-1 text-sm font-medium rounded-full flex-shrink-0 ${badgeClass}`}>{label}</span>
    <div>
      <p className="font-medium text-gray-900">{title}</p>
      <p className="text-sm text-gray-600">{text}</p>
    </div>
  </div>
);

const Faq = ({ q, children }) => (
  <div>
    <h3 className="font-semibold text-gray-900 mb-2">{q}</h3>
    <p className="text-sm text-gray-700">{children}</p>
  </div>
);

const Card = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        {Icon && <Icon className="text-blue-600" size={24} />}
        {title}
      </h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const InvoicesGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Invoicing Guide</h1>
          <p className="text-lg text-gray-600">
            Create invoices, get them to your customers by text, email or print, and track who has paid
          </p>
        </div>

        {/* Why Invoices Matter */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Why Proper Invoicing Matters</h2>
              <p className="text-gray-700">
                Professional invoices help you get paid faster, look more professional, and give you the
                records you need at tax time.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              ['Get Paid Faster', 'Clear invoices with due dates lead to quicker payment'],
              ['Professional Image', 'Detailed invoices build trust and credibility'],
              ['Tax Records', 'Every job and payment is tracked for your tax reports'],
              ['Payment Tracking', 'Know exactly who owes you money'],
            ].map(([title, text]) => (
              <div key={title} className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <p className="font-medium text-gray-900">{title}</p>
                  <p className="text-sm text-gray-700">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Two ways to work */}
        <Card title="Two Ways to Work: Simple or Standard" icon={Settings}>
          <p className="text-gray-700 mb-4">
            SuddyFlow can match how you run your business. Pick your mode in{' '}
            <strong>Settings → Business → How You Work</strong>. You can switch any time, and nothing is
            deleted when you do.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                <Zap size={18} className="text-green-600" /> Simple
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                You agree on the price and date with the customer yourself, and just need a professional invoice
                and tax tracking.
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Menu shows Customers, Jobs, Invoices, Expenses and Reports</li>
                <li>• New Jobs start as Scheduled, with today's date filled in</li>
                <li>• <strong>Completing a job creates the invoice for you</strong></li>
              </ul>
            </div>
            <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                <FileText size={18} className="text-blue-600" /> Standard
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                The full toolkit: send quotes, schedule work, manage leads, agreements and your team.
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Everything in Simple, plus Leads, Agreements, Team and Email Templates</li>
                <li>• New Jobs start as Quoted so you can send a quote first</li>
                <li>• Completed jobs wait in <strong>Ready to Invoice</strong> until you create the invoice</li>
              </ul>
            </div>
          </div>
          <Callout tone="amber">
            The steps below are split by mode. Sending, printing, payments and statuses work the same in both.
          </Callout>
        </Card>

        {/* Simple workflow */}
        <Card title="Simple Mode: From Job to Invoice">
          <div className="space-y-6">
            <Step n="1" title="Add the job">
              <p className="text-gray-700 mb-2">
                Tap <strong>New Job</strong>. Choose the customer, the service, the price you agreed on, and the
                work date. That's it.
              </p>
            </Step>
            <Step n="2" title="Do the work">
              <p className="text-gray-700">The job shows as Scheduled until you complete it.</p>
            </Step>
            <Step n="3" title="Tap Complete">
              <p className="text-gray-700 mb-2">
                Open the job and tap <strong>Complete</strong>. If the customer paid you on the spot, check{' '}
                <strong>"Yes, customer already paid"</strong> and pick how they paid. Otherwise just finish
                the job.
              </p>
              <Callout tone="green">
                <strong>Paid on the spot?</strong> You get a <strong>Paid</strong> invoice and the payment is recorded.
                <br />
                <strong>Not paid yet?</strong> You get an <strong>Unpaid</strong> invoice, due in 14 days.
              </Callout>
            </Step>
            <Step n="4" title="Send or print the invoice" color="bg-green-600">
              <p className="text-gray-700 mb-2">
                The "Job Completed" screen shows your invoice with buttons to <strong>Text Invoice</strong>,{' '}
                <strong>Download / Print PDF</strong> and <strong>Email Invoice</strong>. Missed it? Open{' '}
                <strong>Invoices</strong>, tap the invoice, and use the same buttons.
              </p>
            </Step>
          </div>
        </Card>

        {/* Standard workflow */}
        <Card title="Standard Mode: The Complete Invoicing Workflow">
          <div className="space-y-6">
            <Step n="1" title="Complete the job">
              <p className="text-gray-700 mb-2">
                Open the job, tap <strong>Complete</strong>, and enter the final amount if it differs from the quote.
              </p>
              <Callout tone="blue">
                <strong>Tip:</strong> Completed jobs appear in the "Ready to Invoice" section at the top of the Invoices page.
              </Callout>
            </Step>

            <Step n="2" title="Create the invoice">
              <p className="text-gray-700 mb-2">
                Go to Invoices → find the green "Ready to Invoice" box → tap <strong>Create Invoice</strong> next to the job.
                The amounts fill in for you.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 font-mono">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-semibold">$500.00 (from the job)</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span className="font-semibold">added if you collect sales tax</span>
                </div>
                <div className="flex justify-between">
                  <span>Due Date:</span>
                  <span className="font-semibold">14 days (default, editable)</span>
                </div>
              </div>
            </Step>

            <Step n="3" title="Send it to the customer (optional)">
              <p className="text-gray-700 mb-2">
                Tick "Send invoice email" when you create it, or send it afterwards from the invoice. See
                "Getting Invoices to Customers" below for text, email and print.
              </p>
            </Step>

            <Step n="4" title="Track payment status">
              <p className="text-gray-700 mb-2">
                An invoice stays Unpaid until you record a payment. Your unpaid and overdue totals are always
                at the top of the Invoices page.
              </p>
            </Step>

            <Step n="5" title="Record the payment" color="bg-green-600">
              <p className="text-gray-700 mb-2">
                When the customer pays, open the invoice → <strong>Record Payment</strong> → enter amount, method
                and date.
              </p>
              <Callout tone="green">
                <strong>Automatic:</strong> the status changes to Paid and you can email a receipt.
              </Callout>
            </Step>
          </div>
        </Card>

        {/* Getting invoices to customers */}
        <Card title="Getting Invoices to Customers">
          <p className="text-gray-700 mb-4">
            Open any invoice from the Invoices page to see these options. In Simple mode they also appear right after you complete a job.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Smartphone size={18} className="text-green-600" /> Text Invoice
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Sends your customer a text with a link to their invoice. Great for customers without email.
              </p>
              <p className="text-xs text-gray-500">
                Needs: a phone number on the customer, and texting turned on in Settings → Business → SMS Notifications
                ("Text Quotes &amp; Invoices to Customers"). See the SMS Notifications guide.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Mail size={18} className="text-blue-600" /> Email Invoice
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Emails the invoice with a link to view it online. If you've already sent it, the button says Resend.
              </p>
              <p className="text-xs text-gray-500">Needs: an email address on the customer.</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Download size={18} className="text-gray-700" /> Download / Print PDF
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Saves a professional PDF you can print and hand over, or attach to your own message.
              </p>
              <p className="text-xs text-gray-500">Works for every invoice, with or without an email or phone.</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Link2 size={18} className="text-purple-600" /> Payment Link
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Copies a link where the customer can pay by card.
              </p>
              <p className="text-xs text-gray-500">Needs: card payments connected in Settings → Payments.</p>
            </div>
          </div>
          <div className="mt-4">
            <Callout tone="blue">
              <strong>What customers see:</strong> the link in a text or email opens a simple page showing the amount and status.
              If you've connected card payments, they can pay right there. Otherwise it shows your contact details.
              If you accept Venmo, Zelle and similar, customers can tell you they've paid from the link in the invoice
              email, and it shows up under <strong>Invoices → Reconcile Payments</strong> for you to approve.
            </Callout>
          </div>
        </Card>

        {/* Invoice Statuses Explained */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Invoice Statuses Explained</h2>
          <div className="space-y-4">
            <StatusRow
              label="Unpaid"
              badgeClass="bg-amber-100 text-amber-700"
              title="Waiting for Payment"
              text="Invoice created but not yet paid, and not past its due date."
            />
            <StatusRow
              label="Partial"
              badgeClass="bg-orange-100 text-orange-700"
              title="Partially Paid"
              text="The customer paid some but not all. The remaining balance is shown."
            />
            <StatusRow
              label="Overdue"
              badgeClass="bg-red-100 text-red-700"
              title="Past Due Date"
              text="Payment wasn't received by the due date. Time to follow up!"
            />
            <StatusRow
              label="Paid"
              badgeClass="bg-green-100 text-green-700"
              title="Fully Paid"
              text="Full payment received and recorded. All done!"
            />
          </div>
        </div>

        {/* Understanding Your Numbers */}
        <Card title="Understanding Your Numbers" icon={Calculator}>
          <div className="space-y-4">
            {[
              ['Total Revenue', 'bg-green-50 border-green-200', 'text-green-900', 'text-green-600',
                'Sum of all paid invoices. This is money you have actually received.',
                '10 invoices at $500 each, all paid', '$5,000 revenue'],
              ['Unpaid', 'bg-amber-50 border-amber-200', 'text-amber-900', 'text-amber-600',
                "Money you're still waiting to receive from unpaid, partial and overdue invoices.",
                '5 invoices at $500 each, not paid yet', '$2,500 unpaid'],
              ['Overdue', 'bg-red-50 border-red-200', 'text-red-900', 'text-red-600',
                'Unpaid invoices where the due date has passed. These need attention first.',
                '2 invoices at $500 each, past due', '$1,000 overdue'],
              ['Collection Rate', 'bg-blue-50 border-blue-200', 'text-blue-900', 'text-blue-600',
                'The share of your invoices that have been paid. Higher is better.',
                '8 paid out of 10 invoices', '80% collection rate'],
            ].map(([title, box, exLabel, exValue, text, example, result]) => (
              <div key={title}>
                <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-700 mb-1">{text}</p>
                <div className={`border rounded-lg p-3 text-sm ${box}`}>
                  <strong className={exLabel}>Example:</strong> {example} = <strong className={exValue}>{result}</strong>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recording Payments */}
        <Card title="Recording Payments">
          <div className="space-y-4">
            <p className="text-gray-700">
              When a customer pays you by cash, check, Venmo or any other way, record it so your books stay accurate.
              Open the invoice and tap <strong>Record Payment</strong>.
            </p>

            <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Payment Methods Available:</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {['💵 Cash', '🏦 Check', '💳 Credit Card', '💳 Debit Card', '📱 Venmo', '💸 Zelle', '🌐 PayPal', '🏦 Bank Transfer', '❓ Other'].map((m) => (
                  <div key={m} className="bg-white rounded px-3 py-2">{m}</div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {[
                ['Amount', 'How much they paid (can be partial)'],
                ['Payment Date', 'When you received the payment'],
                ['Method', 'Cash, check, Venmo, etc.'],
                ['Reference #', 'Check number or transaction ID (optional)'],
              ].map(([t, d]) => (
                <div key={t} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 mb-1">{t}</p>
                  <p className="text-xs text-gray-600">{d}</p>
                </div>
              ))}
            </div>

            <Callout tone="blue">
              <strong>Partial payments are fine.</strong> If a customer pays $200 on a $500 invoice, record the $200.
              The invoice shows Partial with $300 remaining. Record the rest when it arrives.
            </Callout>

            <Callout tone="green">
              <strong>Paid on the spot?</strong> You don't need to do this separately. Check "customer already paid" when
              you complete the job and the invoice and payment are created together.
            </Callout>
          </div>
        </Card>

        {/* Sales tax */}
        <Card title="Sales Tax on Invoices">
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              Set up your sales tax in <strong>Settings → Tax</strong>. If you collect sales tax, SuddyFlow adds it to
              invoices at your rate. If you don't, invoices carry no tax.
            </p>
            <ul className="space-y-2">
              <li>• <strong>Quoted a flat price that already includes tax?</strong> On the New Invoice form, check "Quote includes tax" and enter the total. SuddyFlow works out the subtotal and tax for you.</li>
              <li>• <strong>Paid on the spot?</strong> Check "Sales tax was included in this amount" when you complete the job if the customer's payment already covered the tax.</li>
            </ul>
          </div>
        </Card>

        {/* Common Questions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Common Questions</h2>

          <div className="space-y-5">
            <Faq q="Can I edit an invoice after creating it?">
              Yes. Open the invoice and tap the pencil. You can change the amounts, dates, invoice number and notes.
              Once a payment has been recorded, the amounts are locked, but you can still change the due date and notes.
            </Faq>

            <Faq q="What if I need to delete an invoice?">
              You can delete an invoice that has <strong>no payments recorded</strong>. The job goes back to "Ready to
              Invoice" so you can create a new one. Once a payment is recorded the invoice can't be deleted, to keep your
              accounting records correct.
            </Faq>

            <Faq q="I completed a job with the wrong amount. How do I fix it?">
              If the job has no invoice yet, open it, tap Edit, then <strong>Reopen job</strong>, fix the price and
              complete it again. If it has an unpaid invoice, delete the invoice first, then reopen the job. If the
              invoice is already paid, don't reopen it. Record a refund or credit instead, and create a new job for
              any extra work.
            </Faq>

            <Faq q="Do I have to email invoices?">
              No. Text it, email it, or print the PDF and hand it over. You can send or resend any invoice later from the
              invoice itself.
            </Faq>

            <Faq q="The Text Invoice button isn't showing, or it gives an error.">
              The customer needs a phone number saved. Then check Settings → Business → SMS Notifications: the master
              switch must be on, your own mobile number saved, and "Text Quotes &amp; Invoices to Customers" checked.
            </Faq>

            <Faq q="What happens if I don't set a due date?">
              Due dates are optional. Without one, the invoice stays unpaid until paid and never becomes Overdue.
              A due date helps customers know when to pay and helps you spot late payments. New invoices default to 14 days.
            </Faq>

            <Faq q="Can I change the tax rate?">
              Yes. Set your rate in Settings → Tax. See "Sales Tax on Invoices" above.
            </Faq>

            <Faq q="How do customers pay?">
              Most customers pay you directly (cash, check, Venmo, Zelle) and you record it. If you've connected card
              payments in Settings → Payments, customers can also pay by card from the link in their text or email, or
              from a Payment Link you copy yourself.
            </Faq>

            <Faq q="Can I send payment reminders for overdue invoices?">
              You can resend or text any invoice at any time. Automatic payment reminders are available on the Pro plan and
              up, and you set them in Settings → Emails.
            </Faq>

            <Faq q="Who can see invoices?">
              Invoices and payments are visible to the account owner only. Team members can't see them.
            </Faq>

            <Faq q="Can I switch between Simple and Standard?">
              Yes, any time, in Settings → Business → How You Work. Invoices you've already created stay exactly as they are.
              In Standard mode, completed jobs wait in Ready to Invoice instead of being invoiced automatically.
            </Faq>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💡 Best Practices</h2>
          <div className="space-y-4">
            {[
              ['Invoice Right After the Work',
                'Send the invoice the same day. Customers pay fastest while the work is fresh. In Simple mode this happens when you complete the job.'],
              ['Text It Before You Leave',
                "A text with the invoice link is quick, and the customer has it on their phone right away. It's the best option for customers who don't use email."],
              ['Set Clear Due Dates',
                'Use 7 to 14 days for residential customers and 30 days for commercial. Shorter due dates mean faster payment.'],
              ['Follow Up on Overdue Invoices',
                'Check your Invoices page weekly for red Overdue items. A friendly text often gets paid within a day. Most late payments are just forgotten.'],
              ['Accept Multiple Payment Methods',
                'The easier it is to pay, the faster you get paid. Set up the methods you accept in Settings → Payments, and put payment details in the invoice notes.'],
              ['Review Your Numbers Monthly',
                'Check Total Revenue, Collection Rate and Overdue at the end of each month to catch problems early. A good goal is a 90% or better collection rate.'],
            ].map(([t, d]) => (
              <div key={t}>
                <h3 className="font-semibold text-amber-900 mb-1">{t}</h3>
                <p className="text-sm text-amber-800">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Reference</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ['Simple Mode', ['New Job: customer, service, price, date', 'Tap Complete', 'Check "customer already paid" if they paid', 'Text, email or print the invoice']],
              ['Standard Mode', ['Complete the job', 'Find it under "Ready to Invoice"', 'Review and tap Create Invoice', 'Send it, then record the payment']],
              ['Recording Payments', ['Open the invoice', 'Tap "Record Payment"', 'Enter amount, date and method', 'Status updates automatically']],
            ].map(([title, items]) => (
              <div key={title}>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">{title}</h3>
                <div className="space-y-2 text-sm">
                  {items.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-700 mb-4">
            Questions about invoicing? We're here to help you get paid faster!
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Mail size={18} />
            Email Support
          </a>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesGuide;